import React from 'react';
import styled, { keyframes } from 'styled-components';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideIn = keyframes`
  from { transform: translateY(30px); }
  to { transform: translateY(0); }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 2rem;
  backdrop-filter: blur(5px);
  animation: ${fadeIn} 0.3s ease-out;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 20px;
  width: 90%;
  max-width: 1200px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  animation: ${slideIn} 0.3s ease-out;
  padding: 3rem;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${props => props.accentColor};
    border-radius: 4px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #333;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => `${props.accentColor}15`};
    color: ${props => props.accentColor};
  }
`;

const ModalHeader = styled.div`
  margin-bottom: 2rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid #eee;
`;

const ModalTitle = styled.h2`
  color: ${props => props.accentColor};
  font-size: 2.5rem;
  margin-bottom: 1rem;
`;

const Description = styled.p`
  font-size: 1.2rem;
  color: #666;
  line-height: 1.6;
`;

const CapabilitiesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
`;

const CategoryCard = styled.div`
  background: white;
  border-radius: 15px;
  padding: 1.5rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid #eee;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 15px rgba(0, 0, 0, 0.1);
  }
`;

const CategoryTitle = styled.h3`
  color: ${props => props.accentColor};
  font-size: 1.3rem;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid ${props => `${props.accentColor}30`};
`;

const CapabilityList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;

  li {
    margin: 0.8rem 0;
    padding-left: 1.5rem;
    position: relative;
    color: #555;

    &::before {
      content: '→';
      position: absolute;
      left: 0;
      color: ${props => props.accentColor};
    }

    &:hover {
      color: ${props => props.accentColor};
      transform: translateX(5px);
      transition: all 0.3s ease;
    }
  }
`;

const BenefitsSection = styled.div`
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid #eee;
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-top: 1.5rem;
`;

const BenefitCard = styled.div`
  background: ${props => `${props.accentColor}08`};
  padding: 1.5rem;
  border-radius: 12px;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => `${props.accentColor}15`};
    transform: translateY(-3px);
  }
`;

const CustomizationSection = styled.div`
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid #eee;
`;

const CustomizationGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-top: 1.5rem;
`;

const CustomizationItem = styled.div`
  text-align: center;
  padding: 1rem;
  border-radius: 12px;
  background: ${props => `${props.accentColor}08`};
`;

const ServiceModal = ({ service, onClose }) => {
  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()} accentColor={service.accentColor}>
        <CloseButton onClick={onClose} accentColor={service.accentColor}>×</CloseButton>
        
        <ModalHeader>
          <ModalTitle accentColor={service.accentColor}>{service.title}</ModalTitle>
          <Description>{service.description}</Description>
        </ModalHeader>

        {service.modalContent && service.modalContent.detailedFeatures && (
          <CapabilitiesGrid>
            {service.modalContent.detailedFeatures.map((category, index) => (
              <CategoryCard key={index}>
                <CategoryTitle accentColor={service.accentColor}>
                  {category.category}
                </CategoryTitle>
                <CapabilityList accentColor={service.accentColor}>
                  {category.capabilities.map((capability, idx) => (
                    <li key={idx}>{capability}</li>
                  ))}
                </CapabilityList>
              </CategoryCard>
            ))}
          </CapabilitiesGrid>
        )}

        {service.modalContent && service.modalContent.benefits && (
          <BenefitsSection>
            <h3>Key Benefits</h3>
            <BenefitsGrid>
              {service.modalContent.benefits.map((benefit, index) => (
                <BenefitCard key={index} accentColor={service.accentColor}>
                  {benefit}
                </BenefitCard>
              ))}
            </BenefitsGrid>
          </BenefitsSection>
        )}

        {service.modalContent && service.modalContent.customization_options && (
          <CustomizationSection>
            <h3>Customization Options</h3>
            <CustomizationGrid>
              {Object.entries(service.modalContent.customization_options).map(([key, value], index) => (
                <CustomizationItem key={index} accentColor={service.accentColor}>
                  <h4>{key.replace(/_/g, ' ').toUpperCase()}</h4>
                  <p>{Array.isArray(value) ? value.join(', ') : value.toString()}</p>
                </CustomizationItem>
              ))}
            </CustomizationGrid>
          </CustomizationSection>
        )}
      </ModalContent>
    </ModalOverlay>
  );
};

export default ServiceModal; 